import type { DashboardFilterValues } from '@/app/(authed)/(properties)/dashboard/page.tsx';
import Paginator from '@/app/components/Paginator';
import { PropertyCard } from '@/app/components/PropertyCard';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { Loading } from './loading';
import { useMyProperties } from './useAccountProperties';

export interface PropertyListProps {
  filters: DashboardFilterValues;
  activePage: number;
  setActivePage: (page: number) => void;
}

export const PropertyList = (props: PropertyListProps) => {
  const { activePage, setActivePage } = props;
  const { data, isLoading, error } = useMyProperties({
    pagination: { currentPage: activePage, itemsPerPage: 50 },
    companyId: props.filters.company ? String(props.filters.company) : undefined,
    assignedTo: props.filters.assigned ? String(props.filters.assigned) : undefined,
  });

  const ListContent = () => {
    if (isLoading) return <Loading />;

    if (error)
      return (
        <div className="text-center">
          <ShieldExclamationIcon className="mx-auto size-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900">Error Fetching properties</h3>
          <p className="mt-1 text-sm text-gray-500">Try again later</p>
        </div>
      );

    if (!data) {
      return <></>;
    }

    return (
      <>
        {(data.data || []).map(property => (
          <PropertyCard key={property.id} property={property} />
        ))}
      </>
    );
  };

  return (
    <div className="mx-6">
      <div className="text-fiona-neutral flex justify-between text-sm">
        {isLoading ? (
          <p>Loading Results ...</p>
        ) : !error ? (
          <>
            <p>
              Showing {data?.data.length} results out of {data?.meta.totalItems} properties
            </p>
          </>
        ) : (
          <></>
        )}
      </div>
      <ul role="list" className="mt-1 space-y-3">
        <ListContent />
      </ul>
      {!error && (
        <div className="m-6">
          <Paginator
            page={data?.meta.currentPage || 1}
            pageCount={data?.meta.totalPages || 0}
            onPageChange={page => setActivePage(page)}
            totalItems={data?.meta.totalItems || 0}
            itemsPerPage={data?.meta.itemsPerPage || 0}
          />
        </div>
      )}
    </div>
  );
};
